/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import GradingMethod from 'linced-app/enums/grading-method';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  objectAt,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AssessmentOverviewController extends Controller {
  @tracked
  activeGrades;

  @tracked
  activeSlice;

  @tracked
  showDialog;

  @computed('model.{grades.[],modules.[],group.currentLearners.[]}')
  get data() {
    let data = EmberObject.create();
    let learners = this.get('model.group.currentLearners');

    this.get('model.modules').forEach((module) => {
      let moduleData = [];
      let type = 'bar';

      // Get current/latest grades for the module in question.
      let latestGrades = this.get('model.grades').filter((grade) => {
        return grade === this.findGrade(module, grade.get('person'));
      });

      // Handle different grading methods.
      switch (module.get('gradeSchedule.awardMethod')) {
        case GradingMethod.NUMERIC:
          type = 'box';
          moduleData = this.generateBox(module, learners, latestGrades);
          break;
        case GradingMethod.SELECT:
          type = 'pie';
          moduleData = this.generatePie(module, learners, latestGrades);
          break;
        case GradingMethod.MULTIPLE_SELECT:
          type = 'bar';
          moduleData = this.generateBar(module, learners, latestGrades);
          break;
      }

      data.set(module.get('id'), {
        type,
        data: moduleData,
        allData: this.get('model.grades').filter(
          (item) => item.module_id === module.id,
        ),
      });
    });

    return data;
  }

  findGrade(module, person) {
    const moduleId = module.get('id');
    const personId = person.get('id');
    let moduleGrades = this.get('model.grades').filter((item) => {
      return item.module_id === moduleId && item.person_id === personId;
    });
    if (moduleGrades && moduleGrades.length) {
      let sorted = moduleGrades
        .slice()
        .sort((a, b) =>
          a.createTime > b.createTime
            ? 1
            : a.createTime < b.createTime
              ? -1
              : 0,
        );
      return sorted[sorted.length - 1];
    }

    return null;
  }

  generateBar(module, learners, grades) {
    let moduleData = [];

    module.get('gradeSchedule.select.values').forEach((option) => {
      let optionGrades = grades.filter(function (grade) {
        return grade.get('multiSelectAwarded').includes(option.get('value'));
      });
      let count = optionGrades.length;
      let numLearners = learners.length;
      let label = option.get('label');

      let percentage = Math.round((count / numLearners) * 100);
      let pluralised = numLearners === 1 ? 'student' : 'students';
      let tooltip = `<h3>${label}</h3><br>${count} of ${numLearners} ${pluralised} (${percentage}%)<br><br>Click for more info`;

      moduleData.push({
        value: option.get('value'),
        label,
        description: option.get('description'),
        count,
        tooltip,
        learners: mapBy(optionGrades, 'person'),
        moduleLabel: module.get('label'),
        moduleId: module.get('id'),
      });
    });

    return moduleData;
  }

  generateBox(module, learners, grades) {
    let moduleData = [];
    let numericGrades = [];
    grades.forEach((grade) => {
      numericGrades.push(grade.get('numericAwarded'));
    });
    moduleData.push(numericGrades);

    return moduleData;
  }

  generatePie(module, learners, grades) {
    let moduleData = [];
    let total = 0;
    let numLearners = learners.length;

    module.get('gradeSchedule.select.values').forEach((option) => {
      let optionGrades = filterBy(grades, 'selectAwarded', option.get('value'));
      let count = optionGrades.length;
      total += count;
      if (count) {
        let label = option.get('label');
        let percentage = Math.round((count / numLearners) * 100);
        let pluralised = numLearners === 1 ? 'student' : 'students';
        let tooltip = `<h3>${label}</h3><br>${count} of ${numLearners} ${pluralised} (${percentage}%)<br><br>Click for more info`;

        moduleData.push({
          value: option.get('value'),
          label,
          description: option.get('description'),
          count,
          tooltip,
          learners: mapBy(optionGrades, 'person'),
          moduleLabel: module.get('label'),
          moduleId: module.get('id'),
        });
      }
    });

    // If we have any left-over learners, they haven't got a grade (yet).  Count 'em up.
    if (total < numLearners) {
      let ungraded = learners.filter((learner) => {
        return isEmpty(findBy(grades, 'person.id', learner.get('personId')));
      });
      let count = numLearners - total;
      let percentage = Math.round((count / numLearners) * 100);
      let pluralised = numLearners === 1 ? 'student' : 'students';
      let tooltip = `<h3>Not yet marked</h3><br>${count} of ${numLearners} ${pluralised} (${percentage}%)<br><br>Click for more info`;

      moduleData.push({
        value: '',
        label: 'Not marked',
        description: 'Not yet marked',
        count,
        color: '#dadada',
        tooltip,
        learners: mapBy(ungraded, 'person'),
        moduleLabel: module.get('label'),
        moduleId: module.get('id'),
      });
    }

    return moduleData;
  }

  @action
  chartClicked(data) {
    this.activeSlice = data;
    this.activeGrades = this.data.get(data.moduleId).allData;
    this.showDialog = true;
  }

  @action
  closeDialog() {
    this.showDialog = false;
  }

  @action
  sparklineData(personId, grades) {
    let data = [];
    let options = grades[0]?.get('module.gradeSchedule.select.values') ?? [];
    let values = mapBy(options, 'value');
    filterBy(grades, 'person.id', personId)
      .sort(byKeys('createTime'))
      .forEach((grade) => {
        let index = values.indexOf(grade.get('selectAwarded'));
        data.push({
          value: index,
          timestamp: grade.get('createTime'),
          label: objectAt(options, index)?.get('label'),
        });
      });

    return data;
  }
}
